import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import Delete from "@mui/icons-material/Delete";
import FilterList from "@mui/icons-material/FilterList";
import Undo from "@mui/icons-material/Undo";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { Omnisearch } from "components/Omnisearch/Omnisearch";
import pluralize from "pluralize";
import { MouseEvent, useCallback, useMemo, useState } from "react";

import { facilityFilterKeys, filterFacilityRows } from "./credentialFacilityFilter";

/**
 * One linked facility, normalised so both surfaces feed the same grid: the customer
 * dialog resolves these from `facilities`, the staff page from the credential's
 * tenant walk.
 */
export type CredentialFacilityRow = {
  id: string;
  name: string;
  streetAddress1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  lepcName?: string | null;
  /** Rendered in the Organization column, which only the cross-tenant surface shows. */
  organizationName?: string | null;
  /** The Organization filter's value, so a name containing a space stays one token. */
  organizationId?: string | null;
  /**
   * Staged, not yet written. Staff hold facility changes until Save so the whole set
   * can be confirmed at once; the customer surface writes into form state and always
   * passes null.
   */
  staged?: "added" | "removed" | null;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/** `Omnisearch` renders its search field at MUI's default size. */
const SEARCH_FIELD_HEIGHT = 56;

function TruncatedCell({ value }: { value?: string | null }) {
  return (
    <Tooltip title={value ?? ""}>
      <Typography variant="body2" noWrap>
        {value}
      </Typography>
    </Tooltip>
  );
}

function buildColumns({
  showOrganization,
  disabled,
  onToggle,
}: {
  showOrganization: boolean;
  disabled: boolean;
  onToggle: (facilityId: string) => void;
}): GridColDef<CredentialFacilityRow>[] {
  const staged = (row: CredentialFacilityRow) => row.staged ?? null;

  return [
    ...(showOrganization
      ? [
          {
            field: "organizationName",
            headerName: "Organization",
            flex: 0.8,
            renderCell: ({ row }: { row: CredentialFacilityRow }) => (
              <TruncatedCell value={row.organizationName} />
            ),
          },
        ]
      : []),
    {
      field: "name",
      headerName: "Facility",
      flex: 1,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          noWrap
          color={staged(row) === "added" ? "success.main" : "text.primary"}
          sx={{ textDecoration: staged(row) === "removed" ? "line-through" : "none" }}
        >
          {row.name}
          {staged(row) === "added" && " (new)"}
        </Typography>
      ),
    },
    {
      field: "streetAddress1",
      headerName: "Street Address",
      flex: 1,
      valueGetter: (_, row) => {
        if (!row.streetAddress1) return "";
        return [row.streetAddress1, row.city, row.state, row.zip].filter(Boolean).join(", ");
      },
      renderCell: ({ value }) => <TruncatedCell value={value} />,
    },
    {
      field: "state",
      headerName: "State/Territory",
      flex: 0.35,
    },
    {
      field: "lepcName",
      headerName: "LEPC",
      flex: 0.5,
      renderCell: ({ row }) => <TruncatedCell value={row.lepcName} />,
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.15,
      getActions: ({ row }) => {
        const isStagedRemoval = staged(row) === "removed";
        return [
          <Tooltip title={isStagedRemoval ? "Keep this facility" : "Remove facility association"}>
            <GridActionsCellItem
              key={row.id}
              label={isStagedRemoval ? `Keep ${row.name}` : `Unlink ${row.name}`}
              onClick={() => onToggle(row.id)}
              icon={isStagedRemoval ? <Undo /> : <Delete />}
              disabled={disabled}
            />
          </Tooltip>,
        ];
      },
    },
  ];
}

export function CredentialFacilitiesGrid({
  rows,
  loading,
  disabled,
  showOrganization,
  pendingCount = 0,
  onAdd,
  onToggle,
  emptyMessage,
}: {
  rows: CredentialFacilityRow[];
  loading: boolean;
  disabled: boolean;
  /** Cross-tenant surfaces only — every row shares one organization otherwise. */
  showOrganization: boolean;
  pendingCount?: number;
  onAdd: () => void;
  /**
   * Removes the link, or takes back a staged removal. One handler for both, so a
   * staged change stays undoable without a second control.
   */
  onToggle: (facilityId: string) => void;
  emptyMessage?: string;
}) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [omnisearch, setOmnisearch] = useState("");

  const columns = useMemo(
    () => buildColumns({ showOrganization, disabled, onToggle }),
    [showOrganization, disabled, onToggle]
  );

  const filteredRows = useMemo(() => filterFacilityRows(rows, omnisearch), [rows, omnisearch]);

  // Built from the unfiltered rows, so applying one filter doesn't empty another's options.
  const filterKeys = useMemo(
    () => facilityFilterKeys(rows, showOrganization),
    [rows, showOrganization]
  );

  // Back to the first page when the filter changes, or a narrowed set can leave the grid
  // parked on a page that no longer exists.
  const handleOmnisearchChange = useCallback((value: string) => {
    setOmnisearch(value);
    setPaginationModel((previous) => ({ ...previous, page: 0 }));
  }, []);

  // Borderless, and the search field's own height — the default is a small outlined
  // button, which reads as a second control rather than part of the search bar.
  const renderFilterButton = useCallback(
    (onClick: (event: MouseEvent<HTMLButtonElement>) => void) => (
      <Button
        variant="text"
        onClick={onClick}
        startIcon={<FilterList />}
        sx={{ height: SEARCH_FIELD_HEIGHT, px: 2, whiteSpace: "nowrap" }}
      >
        Filters
      </Button>
    ),
    []
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Associated Facilities</Typography>
          {pendingCount > 0 && (
            <Typography variant="caption" color="warning.main">
              {`${pendingCount} unsaved ${pluralize("change", pendingCount)} — save to apply`}
            </Typography>
          )}
        </Stack>
        <Button
          disabled={disabled}
          size="small"
          startIcon={<AddCircleOutlineRounded />}
          variant="text"
          onClick={onAdd}
        >
          Link Facilities
        </Button>
      </Stack>
      <Box sx={{ mb: 2 }}>
        <Omnisearch
          omnisearch={omnisearch}
          setOmnisearch={handleOmnisearchChange}
          // Filtering is a synchronous pass over rows already in memory, so the clear
          // button and the popover's Apply need no separate immediate path.
          setOmnisearchImmediate={handleOmnisearchChange}
          onApplyFilters={handleOmnisearchChange}
          columnFilterKeys={filterKeys}
          showFilterButtonInside={false}
          showExternalFilterButton
          renderFilterButton={renderFilterButton}
          // No `persistenceKey`: saved filters belong to a page, and this grid is inside
          // a dialog opened per credential.
          sx={{ width: "100%" }}
        />
      </Box>
      <Box>
        <DataGrid<CredentialFacilityRow>
          loading={loading}
          rows={filteredRows}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationMode="client"
          noRowsMessage={omnisearch.trim() ? "No facilities match this filter." : emptyMessage}
        />
      </Box>
    </>
  );
}
